import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { HTMLAttributes, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useCompactRootHeader } from './compact-header';

/** Existing features own semantic page headers. Observe lazy replacements and retained roots without
 * importing feature CSS/classes or changing the frozen route/presentation contracts.
 */
export function useSurfaceElement(ref: RefObject<HTMLElement | null>, select: (root: HTMLElement) => HTMLElement | null) {
  const [selection, setSelection] = useState<{ element: HTMLElement | null; title: Element | null }>({ element: null, title: null });
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const update = () => setSelection(previous => {
      const element = select(root), title = element?.querySelector(':is(h1,h2,[role="heading"]):not([data-shell-title])') ?? null;
      return previous.element === element && previous.title === title ? previous : { element, title };
    });
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'aria-label', 'disabled'] });
    return () => observer.disconnect();
  }, [ref, select]);
  return selection.element;
}

/** Ignore inactive content inside the surface; an inactive outer column still retains its header. */
export function activeSurfaceElement(root: HTMLElement, element: HTMLElement): boolean {
  const hidden = element.closest('[hidden], [aria-hidden="true"]');
  return !hidden || !root.contains(hidden);
}
function pageHeader(root: HTMLElement, rootToolbar: boolean) {
  const eligible = (element: HTMLElement) => activeSurfaceElement(root, element) && !element.closest('article, [role="log"], [role="listitem"]');
  const semantic = [...root.querySelectorAll('header')].find(eligible);
  if (semantic || !rootToolbar) return semantic ?? null;
  const title = [...root.querySelectorAll<HTMLHeadingElement>('h1:not([data-shell-title])')].find(eligible);
  if (title?.parentElement && title.parentElement.querySelector('button') && getComputedStyle(title.parentElement).display.includes('flex')) return title.parentElement;
  // Existing section owners also use an actions-only flex row before the list, without a header tag/title.
  const parent = root.querySelector('button')?.parentElement;
  if (!parent || parent.matches('[role="tablist"], [role="radiogroup"], [role="group"]') || !eligible(parent) || !getComputedStyle(parent).display.includes('flex')) return null;
  const controls = [...parent.children].filter(child => !child.matches('[data-shell-title], .shell-header-account'));
  return controls.length >= 2 && controls.every(child => child.matches('button, [role="button"]') || child.querySelector('button') && !child.querySelector('input,textarea,h1,h2,p')) ? parent : null;
}

export function SurfaceChrome({ fallback, leading, rootTitle, compactRoot = false, children, ...props }: HTMLAttributes<HTMLDivElement> & { fallback: ReactNode; leading?: ReactNode; rootTitle?: string; compactRoot?: boolean }) {
  const body = useRef<HTMLDivElement>(null);
  const rootToolbar = rootTitle !== undefined;
  const select = useCallback((root: HTMLElement) => pageHeader(root, rootToolbar), [rootToolbar]);
  const header = useSurfaceElement(body, select);
  const needsTitle = !!rootTitle && !!header && !header.querySelector(':is(h1,h2,[role="heading"]):not([data-shell-title])');
  // An explicit leading slot is available to owners; existing UI NavigationBar uses its toolbar.
  const toolbar = header?.querySelector<HTMLElement>('.hylo-navigation-bar__toolbar') ?? header;
  const target = header?.querySelector<HTMLElement>('[data-shell-header-leading]') ?? toolbar;
  useCompactRootHeader(toolbar, compactRoot);
  useLayoutEffect(() => {
    if (!target || !leading && !needsTitle) return;
    target.setAttribute('data-shell-account-header', '');
    if (needsTitle) target.setAttribute('data-shell-integrated-header', '');
    return () => { target.removeAttribute('data-shell-account-header'); target.removeAttribute('data-shell-integrated-header'); };
  }, [target, leading, needsTitle]);
  return <>
    {!header && fallback}
    <div {...props} ref={body} className="shell-route-content">{children}</div>
    {target && leading && createPortal(<span className="shell-header-account">{leading}</span>, target)}
    {target && needsTitle && createPortal(<h1 className="shell-integrated-title" data-shell-title>{rootTitle}</h1>, target)}
  </>;
}
